import { useEffect, useRef, useState } from "react";
import { getMarketStrip } from "../api/user";
import { polygonCluster, polygonWsSymbol } from "../utils/polygonSymbol";
import { isMarketOpen, stockSession } from "../utils/marketStatus";

// Live data for the Home Markets strip.
//
// The market-strip endpoint supplies each instrument's latest quote and previous
// close (StockController::snapshotQuote — the numbers Trade shows), refetched
// every minute. Prices then stream over the same Polygon FMV websocket the Trade
// screen uses — one socket per cluster (stocks, crypto, forex) — and the % is
// recomputed from prev_close on every tick, so price and % are always one number.
//
// Client: "When the extended session ends, keep the latest available stock
// quote… Keep the displayed percentage change consistent with that price and
// with Trade." While stocks are Closed a stock card ignores ticks and holds the
// endpoint's latest quote. Crypto and forex keep streaming; their prev_close is
// the 00:00 UTC daily open, as in Trade ("BTC/ETH: match Trade").

const REFRESH_MS = 60 * 1000;
const SESSION_TICK_MS = 15 * 1000;
const FLUSH_MS = 250;
const RECONNECT_MS = 3000;

const pct = (price, ref) =>
  Number.isFinite(price) && Number.isFinite(ref) && ref > 0
    ? ((price - ref) / ref) * 100
    : null;

const num = (v) => (v === null || v === undefined ? NaN : Number(v));

export function resolveQuote(item, live, session) {
  if (!item) return { price: null, changePct: null };
  const holdStock = item.category === "stocks" && session === "closed";
  const price = num(holdStock ? item.price : live ?? item.price);
  return { price, changePct: pct(price, num(item.prev_close)) };
}

export default function useMarketStrip(tickers) {
  const [data, setData] = useState(null);
  const [live, setLive] = useState({});
  const [session, setSession] = useState(() => stockSession());
  const [forexOpen, setForexOpen] = useState(() => isMarketOpen("forex"));
  const calendarRef = useRef({});

  // Snapshot + references, refreshed every minute.
  useEffect(() => {
    let cancelled = false;
    const load = () =>
      getMarketStrip()
        .then((res) => {
          if (cancelled || !res) return;
          calendarRef.current = {
            holidays: res.holidays || [],
            early_closes: res.early_closes || {},
          };
          setSession(stockSession(calendarRef.current));
          setData(res);
        })
        .catch(() => {});
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Session status flips on its own at 8:00, 9:30, 16:00 and 20:00 ET.
  useEffect(() => {
    // Forex closes Fri 5:00 PM and reopens Sun 5:00 PM ET.
    const id = setInterval(() => {
      setSession(stockSession(calendarRef.current));
      setForexOpen(isMarketOpen("forex"));
    }, SESSION_TICK_MS);
    return () => clearInterval(id);
  }, []);

  // Live FMV prices.
  const tickerKey = tickers.join(",");
  useEffect(() => {
    const apiKey = process.env.REACT_APP_API_KEY;
    if (!apiKey) return undefined;

    let closed = false;
    const sockets = [];
    const timers = [];
    // ws symbol -> REST ticker, so ticks are stored under the ticker the API uses.
    const bySym = {};
    const pending = {};

    const clusters = {};
    tickerKey.split(",").forEach((t) => {
      const sym = polygonWsSymbol(t);
      bySym[sym] = t;
      const cluster = polygonCluster(t);
      clusters[cluster] = [...(clusters[cluster] || []), sym];
    });

    const connect = (cluster, syms) => {
      const ws = new WebSocket(
        `wss://business.polygon.io/${cluster}?apiKey=${apiKey}`
      );
      sockets.push(ws);
      ws.onopen = () => {
        ws.send(JSON.stringify({ action: "auth", params: apiKey }));
        ws.send(
          JSON.stringify({
            action: "subscribe",
            params: syms.map((s) => `FMV.${s}`).join(","),
          })
        );
      };
      ws.onmessage = (event) => {
        try {
          const msgs = JSON.parse(event.data);
          if (!Array.isArray(msgs)) return;
          for (const msg of msgs) {
            if (msg.ev === "FMV" && bySym[msg.sym] && msg.fmv > 0) {
              pending[bySym[msg.sym]] = msg.fmv;
            }
          }
        } catch {
          /* ignore malformed frames */
        }
      };
      ws.onclose = () => {
        if (!closed) timers.push(setTimeout(() => connect(cluster, syms), RECONNECT_MS));
      };
    };

    Object.entries(clusters).forEach(([cluster, syms]) => connect(cluster, syms));

    // Batch ticks so seven symbols streaming several times a second cost at most
    // four renders a second.
    const flush = setInterval(() => {
      const keys = Object.keys(pending);
      if (!keys.length) return;
      const batch = {};
      keys.forEach((k) => {
        batch[k] = pending[k];
        delete pending[k];
      });
      setLive((prev) => ({ ...prev, ...batch }));
    }, FLUSH_MS);

    return () => {
      closed = true;
      clearInterval(flush);
      timers.forEach(clearTimeout);
      sockets.forEach((ws) => {
        try {
          ws.close();
        } catch {
          /* ignore close errors */
        }
      });
    };
  }, [tickerKey]);

  const byTicker = {};
  (data?.instruments || []).forEach((it) => {
    byTicker[it.ticker] = it;
  });

  return { byTicker, live, session, forexOpen, loaded: !!data };
}
